/* eslint-disable @next/next/no-img-element */
"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faLocationDot } from "@fortawesome/pro-solid-svg-icons";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons";
import {
  ComponentInfosLojaAddressInput,
  ComponentInfosLojaSchedules,
  ComponentInfosLojaSchedulesInput
} from "@/graphql/generated/graphql";
import Base from "@/templates/Base";
import Container from "@/components/Container";
import Schedules from "@/components/Schedules";
import Snackbar from "@/components/shared/Snackbar";
import CobasiCuida from "@/components/CobasiCuida";
import Gallery from "@/components/shared/Gallery";
import type { ImageProps } from "@/components/shared/Gallery/gallery.types";
import Facilities from "@/templates/Facilities";
import { FacilidadeType } from "@/types/facilities";
import { formatZipcode } from "@/utils/helpers";
import styles from "./styles.module.scss";
import Services from "../Services";
import { ServiceProps } from "@/types/services";
import Modal from "@/components/Modal";
import { openAddressOnMap } from "@/utils/maps";
import NearbyStores from "@/components/NearbyStores";
import { StoreListItem } from "@/types/filters";
import { orderStoresByGeo } from "@/utils/geoLocation";
import useShareLink from "@/hooks/useShareLink";

export type StoreDetailProps = {
  name: string;
  description: string;
  slug: string;
  gallery: ImageProps[];
  petanjo: boolean;
  whatsapp?: string;
  phone: string;
  address?: ComponentInfosLojaAddressInput;
  maps: string;
  schedules?: ComponentInfosLojaSchedulesInput;
  facilidades?: FacilidadeType[];
  phoneServices?: string;
  onlineSchedule: boolean;
  descriptionServices?: string;
  schedulesVet?: ComponentInfosLojaSchedules;
  schedulesServices?: ComponentInfosLojaSchedules;
  servicos?: ServiceProps[];
  storeListData: StoreListItem[];
};

export interface ModalProps {
  open: boolean;
  title: string;
  content: React.ReactNode;
}

const StoreDetail = (props: StoreDetailProps) => {
  const {
    slug,
    name,
    gallery,
    description,
    address,
    maps,
    schedules,
    phone,
    whatsapp,
    facilidades,
    petanjo,
    schedulesVet,
    schedulesServices,
    servicos,
    phoneServices,
    descriptionServices,
    onlineSchedule,
    storeListData
  } = props;
  const showSectionFacilities = !!facilidades || !!schedules;

  const { snackbarData, handleShareLink, handleCloseSnackbar } = useShareLink();

  const [storeList, setStoreList] = useState<StoreListItem[]>([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingFetcher, setLoadingFetcher] = useState(true);
  const [modalData, setModalData] = useState<ModalProps>({
    open: false,
    title: "",
    content: null
  });

  useEffect(() => {
    if (address?.lat && address?.long) {
      const validStores =
        storeListData.filter(
          (item) =>
            item?.slug !== slug && item?.address?.lat && item?.address?.long
        ) || [];

      const storesByGeo = orderStoresByGeo({
        storeList: validStores,
        latitude: address.lat,
        longitude: address.long
      });

      setStoreList(storesByGeo.slice(0, 10) as StoreListItem[]);
      setLoadingFetcher(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeListData, address]);

  const handleShowModal = (data: ModalProps) => {
    setModalData(data);
  };

  return (
    <Base isLoading={loadingFetcher}>
      <main className={styles.wrapper}>
        <Container>
          <div className={styles.header}>
            <div className={styles.linkContainer}>
              <Link
                href="/lojas"
                className={`link link-coffee ${styles.link} ${styles["link-to-return"]}`}
              >
                <FontAwesomeIcon
                  className={`link-icon-left ${styles["link-icon-left"]}`}
                  icon={faChevronLeft}
                />
                Voltar para Nossas lojas
              </Link>
            </div>
            <div className={styles.buttons}>
              <button
                onClick={() => handleShareLink({ title: name })}
                className={`link link-coffee ${styles.link}`}
              >
                <FontAwesomeIcon
                  className={`link-icon-left ${styles["link-icon-left"]}`}
                  icon={faShareNodes}
                />
                <span>Compartilhar loja</span>
              </button>
            </div>
            <h1 className={`${styles.title} heading-xs`}>{name}</h1>
          </div>
          {gallery?.length > 0 && <Gallery images={gallery} label={name} />}
          <section className={styles.content}>
            <p className="body-text-md">{description}</p>
            {!!address && (
              <div className={styles["content-address"]}>
                <FontAwesomeIcon icon={faLocationDot} fontSize={24} />
                <address className="body-text-md">
                  {address.street}
                  {address.number && `, ${address.number}`}
                  <br />
                  {address.city}-{address.state}
                  <br />
                  {address && address.zipcode && formatZipcode(address.zipcode)}
                </address>
                <a
                  href={maps || openAddressOnMap({ name, address }) || ""}
                  target="_blank"
                  className={`btn outlined ${styles.btn}`}
                >
                  Mostrar no mapa
                </a>
              </div>
            )}
          </section>
          {showSectionFacilities && (
            <section className={styles.facilities}>
              {schedules && (
                <div className={styles.schedules}>
                  <Schedules
                    schedules={schedules}
                    phone={phone}
                    whatsapp={whatsapp}
                  />
                </div>
              )}
              <div className={styles.facilidades}>
                {facilidades && facilidades?.length > 0 && (
                  <Facilities
                    facilidades={facilidades}
                    servicos={servicos || []}
                    onShowModal={handleShowModal}
                  />
                )}
              </div>
            </section>
          )}
        </Container>

        {servicos && servicos.length > 0 && (
          <div className={styles.servicesTarget}>
            <Container>
              <Services
                isPetanjo={!!petanjo}
                schedulesVet={schedulesVet}
                schedulesServices={schedulesServices}
                servicos={servicos}
                phoneServices={phoneServices}
                descriptionServices={descriptionServices}
                onlineSchedule={onlineSchedule}
              />
            </Container>
          </div>
        )}
        <Container>
          {storeList.length > 0 && address?.lat && address?.long && (
            <section className={styles.nearbyStores}>
              <div>
                <h1 className={`${styles.title} heading-xs`}>Lojas próximas</h1>
                <NearbyStores address={address} stores={storeList} />
              </div>
            </section>
          )}

          <section className={styles.cobasiCuida}>
            <div>
              <h1 className={`${styles.title} heading-xs`}>Cobasi Cuida </h1>
              <CobasiCuida />
            </div>
          </section>
        </Container>
      </main>
      <Modal
        open={modalData.open}
        onClose={() => setModalData({ ...modalData, open: false })}
        title={modalData.title}
        content={modalData.content}
      />
      <Snackbar
        theme="dark"
        onCloseButton={handleCloseSnackbar}
        {...snackbarData}
      />
    </Base>
  );
};

export default StoreDetail;
